import { useEffect, useState, type ReactNode } from 'react';
import type { Goal } from '../lib/types';
import { FALLBACK_CAT } from '../lib/types';
import { prettyDay, todayKey } from '../lib/date';
import { useStore } from '../lib/store';
import { CategoryIcon } from './CategoryIcon';
import { LeafBurst } from './LeafBurst';
import { PixelCheckbox } from './PixelCheckbox';
import { StreakDot } from './StreakDot';

export function GoalRow({
  goal,
  done,
  streak,
  onToggle,
  onOpen,
  subtitle,
}: {
  goal: Goal;
  done: boolean;
  streak: number;
  onToggle: () => void;
  onOpen: () => void;
  /** Overrides the default streak/due-date subtitle (e.g. "tended <date>"). */
  subtitle?: ReactNode;
}) {
  const { categoryMap } = useStore();
  const cat = categoryMap.get(goal.cat) ?? FALLBACK_CAT;
  const [burstId, setBurstId] = useState<number | null>(null);

  useEffect(() => {
    if (burstId === null) return;
    const t = window.setTimeout(() => setBurstId(null), 600);
    return () => window.clearTimeout(t);
  }, [burstId]);

  return (
    <div
      className="goal-row"
      onClick={onOpen}
      onKeyDown={e => {
        if (e.key === 'Enter') onOpen();
      }}
      tabIndex={0}
      role="button"
    >
      <span className="goal-row__check">
        <PixelCheckbox
          checked={done}
          ariaLabel={`mark ${goal.title} ${done ? 'incomplete' : 'complete'}`}
          onClick={e => {
            e.stopPropagation();
            if (!done && goal.type === 'habit') setBurstId(Date.now());
            onToggle();
          }}
        />
        {burstId !== null && <LeafBurst key={burstId} />}
      </span>
      <div className="goal-row__title">
        <div className={`line ${done ? 'line--done' : ''}`}>{goal.title}</div>
        <div className="goal-row__subtitle">
          {subtitle ?? (goal.type === 'habit' ? (
            <>
              <StreakDot streak={streak} />
              {streak} day streak
            </>
          ) : (
            todoSubtitle(goal)
          ))}
        </div>
      </div>
      <span className={`tag tag--${cat.tint}`}>
        <CategoryIcon slug={cat.icon} size={14} alt="" />
        {cat.label}
      </span>
    </div>
  );
}

function todoSubtitle(goal: Goal): string {
  if (!goal.due_date) return 'one-off errand';
  const today = todayKey();
  if (goal.due_date === today) return 'due today';
  if (goal.due_date > today) return `due ${prettyDay(goal.due_date)}`;
  return `was due ${prettyDay(goal.due_date)}`;
}
